import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Badge, Form, Modal } from 'react-bootstrap';
import { getDriverDocuments, uploadDriverDocument, getDriverStatus } from '../../api';
import { getVehicleTypes, saveOnboardingVehicle } from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../store/useAuthStore';
import { toast } from 'react-toastify';
import { FileText, CheckCircle2, AlertTriangle, Clock, LogOut, Camera, Shield, Car, Save } from 'lucide-react';
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';

const DOCUMENT_TYPES = [
  // --- OBLIGATORIOS (9) ---
  {
    type: 'licencia_frontal',
    legacyType: 'licencia',
    title: 'Licencia de Conducir (Frente)',
    description: 'Foto frontal nítida de tu licencia de conducir profesional vigente.',
    icon: <FileText size={32} className="text-primary" />,
    required: true
  },
  {
    type: 'licencia_reverso',
    title: 'Licencia de Conducir (Reverso)',
    description: 'Foto del reverso de tu licencia donde se vea claramente la fecha de vencimiento.',
    icon: <FileText size={32} className="text-primary" />,
    required: true
  },
  {
    type: 'rut_frontal',
    legacyType: 'rut',
    title: 'Documento de Identidad / DNI (Frente)',
    description: 'Foto frontal nítida de tu Cédula de Identidad, DNI o documento vigente.',
    icon: <FileText size={32} className="text-info" />,
    required: true
  },
  {
    type: 'rut_reverso',
    title: 'Documento de Identidad / DNI (Reverso)',
    description: 'Foto del reverso nítida de tu Cédula de Identidad, DNI o documento vigente.',
    icon: <FileText size={32} className="text-info" />,
    required: true
  },
  {
    type: 'antecedentes',
    title: 'Certificado de Antecedentes',
    description: 'Certificado oficial de antecedentes penales vigente (emisión menor a 30 días).',
    icon: <FileText size={32} className="text-warning" />,
    required: true
  },
  {
    type: 'soap',
    title: 'Seguro Obligatorio (SOAP o equivalente)',
    description: 'Póliza o seguro obligatorio vigente del vehículo en tu país.',
    icon: <Shield size={32} className="text-success" />,
    required: true
  },
  {
    type: 'permiso_circulacion',
    title: 'Permiso de Circulación / Cédula Vehicular',
    description: 'Permiso de circulación o cédula de habilitación vigente del vehículo.',
    icon: <Car size={32} className="text-primary" />,
    required: true
  },
  {
    type: 'revision_tecnica',
    title: 'Revisión Técnica / VTV / ITV',
    description: 'Certificado de revisión o verificación técnica vehicular al día.',
    icon: <FileText size={32} className="text-info" />,
    required: true
  },
  {
    type: 'foto_vehiculo',
    title: 'Fotos del Vehículo',
    description: 'Fotografía nítida del vehículo donde la placa patente sea claramente visible.',
    icon: <Camera size={32} className="text-success" />,
    required: true
  },
  // --- ADICIONALES / VEHÍCULO (3) ---
  {
    type: 'decreto_mtt',
    legacyType: 'certificado_mtt',
    title: 'Decreto MTT (Decreto 80/212)',
    description: 'Cartón de recorrido o certificado del Ministerio de Transportes.',
    icon: <Car size={32} className="text-secondary" />,
    required: false
  },
  {
    type: 'seguro_asientos',
    title: 'Seguro de Asientos / Pasajeros',
    description: 'Póliza de seguro de asientos para pasajeros del vehículo.',
    icon: <Shield size={32} className="text-info" />,
    required: false
  },
  {
    type: 'padron',
    title: 'Padrón del Vehículo',
    description: 'Certificado de inscripción (Padrón) del vehículo en el Registro Civil.',
    icon: <FileText size={32} className="text-secondary" />,
    required: false
  }
];

const DriverOnboarding = () => {
  const { logout, user } = useAuthStore();
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  const [driverStatus, setDriverStatus] = useState({ is_approved: false });
  const navigate = useNavigate();
  const location = useLocation();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [activeDocType, setActiveDocType] = useState(null);

  // Ficha de Vehículo State
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [plate, setPlate] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [color, setColor] = useState('');
  const [vehicleTypeId, setVehicleTypeId] = useState('');
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [vehicleSaved, setVehicleSaved] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const isUpdateMode = searchParams.get('mode') === 'update';

  const loadData = React.useCallback(async () => {
    try {
      // 1. Cargar tipos de vehículo
      const vTypes = await getVehicleTypes();
      if (Array.isArray(vTypes)) setVehicleTypes(vTypes);

      // 2. Obtener estado de aprobación y chofer
      const statusRes = await getDriverStatus();
      setDriverStatus(statusRes);
      if (statusRes.is_approved && !isUpdateMode) {
        toast.success("¡Tu cuenta ha sido aprobada!");
        navigate('/driver');
        return;
      }

      // Si el chofer ya tiene vehículo registrado, cargarlo
      if (statusRes.assigned_vehicle_plate || statusRes.vehicle_plate) {
        setPlate(statusRes.assigned_vehicle_plate || statusRes.vehicle_plate || '');
        setMake(statusRes.vehicle_make || '');
        setModel(statusRes.vehicle_model || '');
        setVehicleTypeId(statusRes.vehicle_type_id || '');
        setVehicleSaved(true);
      }

      // 3. Obtener documentos subidos (con compatibilidad para claves antiguas: licencia -> licencia_frontal, rut -> rut_frontal, certificado_mtt -> decreto_mtt)
      const docsRes = await getDriverDocuments();
      const docsMap = {};
      docsRes.forEach(doc => {
        docsMap[doc.document_type] = doc;
      });
      DOCUMENT_TYPES.forEach(def => {
        if (!docsMap[def.type] && def.legacyType && docsMap[def.legacyType]) {
          docsMap[def.type] = docsMap[def.legacyType];
        }
      });
      setDocuments(docsMap);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar la información de onboarding.");
    } finally {
      setLoading(false);
    }
  }, [navigate, isUpdateMode]);

  useEffect(() => {
    loadData();
    if (isUpdateMode) return;

    // Poll driver approval status cada 15s
    const interval = setInterval(async () => {
      try {
        const statusRes = await getDriverStatus();
        if (statusRes.is_approved) {
          clearInterval(interval);
          toast.success("¡Tu cuenta ha sido aprobada!");
          navigate('/driver');
        }
      } catch (e) {
        console.error("Polling error:", e);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [loadData, navigate, isUpdateMode]);

  const handleSaveVehicle = async (e) => {
    e.preventDefault();
    if (!plate.trim()) {
      toast.warn("Por favor ingresa la Placa Patente de tu vehículo.");
      return;
    }
    setSavingVehicle(true);
    try {
      await saveOnboardingVehicle({
        plate: plate.trim().toUpperCase(),
        make: make.trim(),
        model: model.trim(),
        year: parseInt(year) || new Date().getFullYear(),
        color: color.trim(),
        vehicle_type_id: vehicleTypeId ? parseInt(vehicleTypeId) : undefined
      });
      toast.success("🚘 Ficha del vehículo guardada con éxito.");
      setVehicleSaved(true);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(`⚠️ Error al guardar vehículo: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSavingVehicle(false);
    }
  };

  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingType(documentType);
    try {
      await uploadDriverDocument(documentType, file);
      toast.success("Documento subido y procesado con éxito.");
      await loadData();
    } catch (err) {
      console.error(err);
    } finally {
      setUploadingType(null);
    }
  };

  const handleCameraCapture = async (documentType) => {
    setShowUploadModal(false);
    try {
      const image = await CapCamera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Prompt,
        promptLabelHeader: 'Subir Documento',
        promptLabelPhoto: 'Elegir de Galería',
        promptLabelPicture: 'Tomar Fotografía'
      });

      if (image) {
        setUploadingType(documentType);
        const byteCharacters = atob(image.base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: `image/${image.format}` });
        const file = new File([blob], `document_${documentType}_${Date.now()}.${image.format}`, { type: `image/${image.format}` });

        await uploadDriverDocument(documentType, file);
        toast.success("Documento capturado y subido con éxito.");
        await loadData();
      }
    } catch (err) {
      console.error("Camera Onboarding Error:", err);
    } finally {
      setUploadingType(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openUploadModalFor = (docType) => {
    setActiveDocType(docType);
    setShowUploadModal(true);
  };

  // Contar cuántos de los 7 requeridos están aprobados
  const requiredTypes = DOCUMENT_TYPES.filter(d => d.required);
  const approvedRequiredCount = requiredTypes.filter(d => documents[d.type] && documents[d.type].status === 'approved').length;
  const isAwaitingVerification = approvedRequiredCount === requiredTypes.length && !driverStatus.is_approved;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0f172a', color: '#f8fafc', paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
      <Container>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            {/* Header del Portal de Onboarding */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-secondary">
              <div>
                <h1 className="fw-bold tracking-tight mb-1 text-primary">Onboarding Conductor</h1>
                <p className="text-muted mb-0">Hola, {driverStatus.full_name || user?.name}</p>
              </div>
              <div className="d-flex gap-2">
                {driverStatus.is_approved && (
                  <Button variant="outline-primary" className="d-flex align-items-center gap-2 fw-bold" onClick={() => navigate('/driver')}>
                    Volver al Portal
                  </Button>
                )}
                <Button variant="outline-danger" className="d-flex align-items-center gap-2" onClick={handleLogout}>
                  <LogOut size={16} /> Salir
                </Button>
              </div>
            </div>

            {/* Banner de Estado General */}
            {driverStatus.is_approved ? (
              <Card className="border-0 mb-4 shadow-lg" style={{ backgroundColor: '#064e3b', borderLeft: '5px solid #10b981' }}>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-3">
                    <CheckCircle2 className="text-success flex-shrink-0" size={32} style={{ color: '#34d399' }} />
                    <div>
                      <h5 className="fw-bold text-white mb-1">Cuenta Aprobada y Activa</h5>
                      <p className="text-slate-300 small mb-0">
                        Tu cuenta está aprobada. Aquí puedes actualizar tus documentos que hayan vencido o requieran renovación. Tu acceso al portal sigue activo.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ) : isAwaitingVerification ? (
              <Card className="border-0 mb-4 shadow-lg text-center" style={{ backgroundColor: '#1e1b4b', borderLeft: '5px solid #6366f1' }}>
                <Card.Body className="p-4">
                  <Clock className="text-indigo-400 mb-3" size={48} style={{ color: '#818cf8' }} />
                  <h4 className="fw-bold text-white mb-2">Documentación en Revisión</h4>
                  <p className="text-slate-300 small mb-0">
                    Has cargado todos los documentos obligatorios. Nuestro equipo está revisando tus datos y documentos para activar tu cuenta.
                  </p>
                </Card.Body>
              </Card>
            ) : (
              <Card className="border-0 mb-4 shadow-lg" style={{ backgroundColor: '#1e293b', borderLeft: '5px solid #ef4444' }}>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-3">
                    <AlertTriangle className="text-danger flex-shrink-0" size={32} />
                    <div>
                      <h5 className="fw-bold text-white mb-1">Registro de Vehículo y Documentos</h5>
                      <p className="text-slate-300 small mb-0">
                        De acuerdo a la regulación de transporte, debes registrar la información de tu vehículo y subir los {requiredTypes.length} documentos obligatorios (incluyendo frente y reverso de tu Licencia y Cédula) para empezar a recibir servicios.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* SECCIÓN 1: FICHA DEL VEHÍCULO */}
            <Card className="border-0 mb-4 shadow-lg" style={{ backgroundColor: '#1e293b' }}>
              <Card.Header className="bg-dark text-white fw-bold d-flex justify-content-between align-items-center py-3">
                <span className="d-flex align-items-center gap-2">
                  <Car size={20} className="text-primary" /> 🚘 Ficha de tu Vehículo
                </span>
                {vehicleSaved && <Badge bg="success" className="px-2 py-1">Registrado</Badge>}
              </Card.Header>
              <Card.Body className="p-4">
                <Form onSubmit={handleSaveVehicle}>
                  <Row className="g-3 mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-slate-300 text-uppercase">Placa Patente *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ej: ABCD12"
                          value={plate}
                          onChange={e => setPlate(e.target.value.toUpperCase())}
                          required
                          className="font-monospace fw-bold bg-dark text-white border-secondary"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-slate-300 text-uppercase">Marca *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ej: Hyundai"
                          value={make}
                          onChange={e => setMake(e.target.value)}
                          required
                          className="bg-dark text-white border-secondary"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-slate-300 text-uppercase">Modelo *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ej: H1"
                          value={model}
                          onChange={e => setModel(e.target.value)}
                          required
                          className="bg-dark text-white border-secondary"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="g-3 mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-slate-300 text-uppercase">Año</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="2022"
                          value={year}
                          onChange={e => setYear(e.target.value)}
                          className="bg-dark text-white border-secondary"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-slate-300 text-uppercase">Color</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ej: Negro"
                          value={color}
                          onChange={e => setColor(e.target.value)}
                          className="bg-dark text-white border-secondary"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-slate-300 text-uppercase">Categoría Vehículo *</Form.Label>
                        <Form.Select
                          value={vehicleTypeId}
                          onChange={e => setVehicleTypeId(e.target.value)}
                          required
                          className="bg-dark text-white border-secondary"
                        >
                          <option value="">Seleccionar Categoría...</option>
                          {vehicleTypes.map(vt => (
                            <option key={vt.id} value={vt.id}>
                              {vt.name} ({vt.capacity || 4} pax)
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="text-end">
                    <Button type="submit" variant="primary" className="fw-bold" disabled={savingVehicle}>
                      {savingVehicle ? <Spinner size="sm" animation="border" /> : <><Save size={16} className="me-1" /> Guardar Ficha Vehículo</>}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* SECCIÓN 2: DOCUMENTOS AUDITABLES */}
            <Card className="border-0 shadow-lg" style={{ backgroundColor: '#1e293b' }}>
              <Card.Header className="bg-dark text-white fw-bold d-flex justify-content-between align-items-center py-3">
                <span>📑 Documentación Legal y Operativa</span>
                <Badge bg="info" className="px-3 py-2">
                  {approvedRequiredCount} de {requiredTypes.length} Obligatorios Aprobados
                </Badge>
              </Card.Header>
              <Card.Body className="p-4">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted small">Cargando requerimientos de documentación...</p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {DOCUMENT_TYPES.map((docDef) => {
                      const doc = documents[docDef.type];
                      const isApproved = doc && doc.status === 'approved';
                      const isPending = doc && doc.status === 'pending';
                      const isRejected = doc && doc.status === 'rejected';

                      return (
                        <Col key={docDef.type} md={6}>
                          <Card
                            className="h-100 border-0 shadow-sm"
                            style={{
                              backgroundColor: isApproved ? '#064e3b' : isRejected ? '#451a1a' : '#0f172a',
                              borderLeft: `4px solid ${isApproved ? '#10b981' : isRejected ? '#ef4444' : isPending ? '#f59e0b' : '#64748b'}`
                            }}
                          >
                            <Card.Body className="p-3 d-flex flex-column justify-content-between">
                              <div>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div className="d-flex align-items-center gap-2">
                                    {docDef.icon}
                                    <div>
                                      <h6 className="fw-bold text-white mb-0">{docDef.title}</h6>
                                      {docDef.required ? (
                                        <small className="text-danger fw-bold">* Obligatorio</small>
                                      ) : (
                                        <small className="text-muted">Opcional</small>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    {isApproved && <Badge bg="success">Aprobado</Badge>}
                                    {isPending && <Badge bg="warning" text="dark">En Revisión</Badge>}
                                    {isRejected && <Badge bg="danger">Rechazado</Badge>}
                                    {!doc && <Badge bg="secondary">Pendiente</Badge>}
                                  </div>
                                </div>
                                <p className="text-slate-300 small mb-3" style={{ fontSize: '11px' }}>{docDef.description}</p>

                                {isRejected && doc.rejection_reason && (
                                  <div className="p-2 mb-3 rounded bg-black bg-opacity-50 border border-danger text-danger small">
                                    <strong>Motivo de Rechazo:</strong> {doc.rejection_reason}
                                  </div>
                                )}
                              </div>

                              <div>
                                <Button
                                  variant={isApproved ? "outline-success" : isRejected ? "danger" : "primary"}
                                  size="sm"
                                  className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                                  onClick={() => openUploadModalFor(docDef.type)}
                                  disabled={uploadingType === docDef.type}
                                >
                                  {uploadingType === docDef.type ? (
                                    <Spinner size="sm" animation="border" />
                                  ) : (
                                    <>
                                      <Camera size={16} />
                                      {doc ? (isApproved ? 'Actualizar Documento' : 'Volver a Subir') : 'Subir Documento'}
                                    </>
                                  )}
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal de Selección de Método de Carga */}
        <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered size="sm">
          <Modal.Header closeButton className="bg-dark text-white border-secondary">
            <Modal.Title className="fs-6 fw-bold">Opciones de Carga</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-dark text-white p-3 space-y-2">
            <Button
              variant="primary"
              className="w-100 d-flex align-items-center justify-content-center gap-2 mb-2 py-2 fw-bold"
              onClick={() => handleCameraCapture(activeDocType)}
            >
              <Camera size={18} /> Usar Cámara o Galería
            </Button>
            <Form.Group>
              <Form.Label className="btn btn-outline-light w-100 d-flex align-items-center justify-content-center gap-2 py-2 mb-0 fw-bold cursor-pointer">
                <FileText size={18} /> Elegir Archivo / PDF
                <Form.Control
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => {
                    setShowUploadModal(false);
                    handleFileUpload(e, activeDocType);
                  }}
                  style={{ display: 'none' }}
                />
              </Form.Label>
            </Form.Group>
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default DriverOnboarding;
